import React, { useState, useEffect, useRef, useContext } from "react";
import "./akcioniplanovi.css";
import { baseRequest } from "../../../axiosconfig";
import Loading from "../../loading";
import DropDown from "../../elements/dropDown";
import { useReactToPrint } from "react-to-print";
import { UserContext } from "../../../userContext";
import { ROLES } from "../../../constants/roles";
import { formatDeadline } from "../../../utils/deadlineFormat";

const STATUS_META = {
  probijen_rok: { label: "Probijen rok", color: "#FF5C5C" },
  istice_uskoro: { label: "Ističe uskoro", color: "#FFD700" },
  u_roku: { label: "U roku", color: "#B0E0E6" },
  bez_roka: { label: "Bez roka", color: "#E0E0E0" },
  zavrseno: { label: "Završeno", color: "#C6EFCE" },
};

const typeFilterList = [
  { id: "mera", name: "Mera (formular rizika)" },
  { id: "aktivnost", name: "Aktivnost (mapa procesa)" },
];

function AkcioniPlanovi() {
  const { user } = useContext(UserContext);
  const canEdit = user && (user.role_id === ROLES.SUPER_ADMIN || user.role_id === ROLES.MANAGER);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [statusFilter, setStatusFilter] = useState(null);
  const [typeFilter, setTypeFilter] = useState({ id: "all", name: "ALL" });
  const componentRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  const loadData = () => {
    setLoading(true);
    baseRequest
      .get("/action_plans")
      .then((res) => {
        setRows(res.data.action_plans || []);
        setSummary(res.data.summary || {});
        setLoading(false);
      })
      .catch(() => {
        setRows([]);
        setLoading(false);
      });
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleToggleCompleted = (row) => {
    const completed = row.computed_status !== "zavrseno";
    setLoading(true);
    baseRequest
      .patch(`/risk_form/${row.risk_form_id}/measure`, { completed })
      .then(() => loadData())
      .catch(() => setLoading(false));
  };

  const filtered = rows.filter((r) => {
    if (statusFilter && r.computed_status !== statusFilter) return false;
    if (typeFilter.id !== "all" && r.type !== typeFilter.id) return false;
    return true;
  });

  // brojači statusa prate filter tipa (ali ne i klik na sam status)
  const typeFiltered = rows.filter(
    (r) => typeFilter.id === "all" || r.type === typeFilter.id
  );
  const badgeCounts = {};
  for (const r of typeFiltered) {
    badgeCounts[r.computed_status] = (badgeCounts[r.computed_status] || 0) + 1;
  }

  const renderTable = (list, withActions) => (
    <table className="akcioni-planovi-table">
      <thead>
        <tr>
          <th style={{ width: "50px" }}>R.BR.</th>
          <th style={{ width: "90px" }}>TIP</th>
          <th style={{ width: "180px" }}>NAZIV</th>
          <th>OPIS MERE/KONTROLE</th>
          <th style={{ width: "160px" }}>PROCES</th>
          <th style={{ width: "150px" }}>ODGOVORAN</th>
          <th style={{ width: "100px" }}>ROK</th>
          <th style={{ width: "110px" }}>STATUS ROKA</th>
          <th style={{ width: "140px" }}>STATUS IZVRŠENJA</th>
          {withActions && <th style={{ width: "120px" }}>AKCIJE</th>}
        </tr>
      </thead>
      <tbody>
        {list.length === 0 && (
          <tr>
            <td colSpan={withActions ? 10 : 9} className="akcioni-planovi-empty">
              Nema mera ni aktivnosti za prikaz.
            </td>
          </tr>
        )}
        {list.map((row, i) => {
          const meta = STATUS_META[row.computed_status] || {};
          return (
            <tr key={row.id}>
              <td>{i + 1}</td>
              <td>{row.type === "mera" ? "Mera" : "Aktivnost"}</td>
              <td>{row.name}</td>
              <td>{row.description || "—"}</td>
              <td>{row.process_name || "—"}</td>
              <td>{row.owners || "—"}</td>
              <td>{formatDeadline(row.deadline)}</td>
              <td>
                <span
                  className="akcioni-planovi-chip"
                  style={{ backgroundColor: meta.color }}
                >
                  {meta.label}
                </span>
              </td>
              <td>{row.status_text || "—"}</td>
              {withActions && (
                <td>
                  {row.type === "mera" && canEdit && (
                    <label className="akcioni-planovi-complete">
                      <input
                        type="checkbox"
                        checked={row.computed_status === "zavrseno"}
                        onChange={() => handleToggleCompleted(row)}
                      />
                      Završeno
                    </label>
                  )}
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <div className="akcioni-planovi">
      <div className="processes-header akcioni-planovi-header">
        <DropDown
          dropDownStyle={{ borderRight: "1px solid var(--mainColor)", width: "35%" }}
          height={"49px"}
          list={typeFilterList}
          onChange={({ value }) => setTypeFilter(value)}
          selectedValue={typeFilter?.name}
          isFilter={true}
        />
        <div style={{ marginRight: "10px", marginLeft: "auto" }}>
          <button className="add-btn-large" onClick={handlePrint}>
            <img alt="" src="/images/printer.png"></img>
          </button>
        </div>
      </div>

      <h2 className="akcioni-planovi-title">AKCIONI PLANOVI — PRAĆENJE MERA I ROKOVA</h2>

      <div className="akcioni-planovi-badges">
        {Object.entries(STATUS_META).map(([key, meta]) => (
          <div
            key={key}
            className={
              statusFilter === key
                ? "akcioni-planovi-badge akcioni-planovi-badge-active"
                : "akcioni-planovi-badge"
            }
            style={{ borderColor: meta.color }}
            onClick={() => setStatusFilter(statusFilter === key ? null : key)}
          >
            <span
              className="akcioni-planovi-badge-dot"
              style={{ backgroundColor: meta.color }}
            ></span>
            {meta.label}: {badgeCounts[key] || 0}
          </div>
        ))}
      </div>

      <div className="akcioni-planovi-table-wrapper">
        {renderTable(filtered, true)}
      </div>

      <div style={{ display: "none" }}>
        <div ref={componentRef} className="akcioni-planovi-print">
          <h2>AKCIONI PLANOVI — PRAĆENJE MERA I ROKOVA</h2>
          <p>Datum: {new Date().toLocaleDateString("sr-RS")}</p>
          {renderTable(filtered, false)}
        </div>
      </div>

      <Loading isLoading={loading} />
    </div>
  );
}

export default AkcioniPlanovi;
